import { queryOptions, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { companySlug } from "@/lib/companySlug";
import { brandName } from "@/lib/brandName";

export interface CompanyAdvertiser {
  id: string;
  program_name: string | null;
  title: string | null;
  description: string | null;
  logo_url: string | null;
  tracking_url: string | null;
}

export interface CompanyPlan {
  id: string;
  contract_type: string | null;
  area: string | null;
  campaign_title: string | null;
  campaign_text: string | null;
  description: string | null;
  term_text: string | null;
  price_ore_kwh: number | null;
  monthly_fee: number | null;
  binding_months: number | null;
  notice_period_days: number | null;
  valid_to: string | null;
  discount_price_ore: number | null;
  discount_monthly_fee: number | null;
  discount_text: string | null;
  comparative_price: number | null;
  comparative_price_2000: number | null;
  comparative_price_5000: number | null;
  comparative_price_20000: number | null;
  comparative_prices_by_area: unknown;
  usps: string[] | null;
  pills: string[] | null;
}

export interface CompanyOffersResult {
  advertiser: CompanyAdvertiser | null;
  plans: CompanyPlan[];
}

const PLAN_FIELDS =
  "id, contract_type, area, campaign_title, campaign_text, description, term_text, price_ore_kwh, monthly_fee, binding_months, notice_period_days, valid_to, discount_price_ore, discount_monthly_fee, discount_text, comparative_price, comparative_price_2000, comparative_price_5000, comparative_price_20000, comparative_prices_by_area, usps, pills";

const STALE_TIME = 5 * 60 * 1000;

/** Jämförbar nyckel: gemener, utan tecken och utan bolagssuffix (SE, AB ...). */
const matchKey = (value: string | null | undefined) =>
  companySlug(value)
    .replace(/-/g, "")
    .replace(/(se|sverige|ab|nordic)$/, "");

/** Hämtar annonsör + aktiva godkända kampanjer för ett bolagsslug. */
export async function fetchCompanyOffers(
  slug: string,
  name?: string | null,
): Promise<CompanyOffersResult> {
  const { data } = await supabase
    .from("adtraction_offers")
    .select("id, program_name, title, description, logo_url, tracking_url")
    .eq("is_active", true);

  const offers = (data as CompanyAdvertiser[] | null) ?? [];
  const keys = [slug, name].filter(Boolean).map((v) => matchKey(v as string));
  const advertiser =
    offers.find(
      (o) => companySlug(brandName(o.program_name, o.title)) === slug,
    ) ??
    offers.find((o) => companySlug(o.program_name ?? o.title) === slug) ??
    offers.find((o) => {
      const key = matchKey(brandName(o.program_name, o.title));
      return keys.some(
        (k) =>
          k.length > 3 && (k === key || k.startsWith(key) || key.startsWith(k)),
      );
    }) ??
    null;

  if (!advertiser) return { advertiser: null, plans: [] };

  const { data: planRows } = await supabase
    .from("offer_plans")
    .select(PLAN_FIELDS)
    .eq("offer_id", advertiser.id)
    .eq("is_active", true)
    .eq("approval_status", "approved")
    .order("sort_order", { ascending: true });

  return {
    advertiser,
    plans: (planRows as unknown as CompanyPlan[] | null) ?? [],
  };
}

export const companyOffersQueryOptions = (slug: string, name?: string | null) =>
  queryOptions({
    // Nyckeln är medvetet bara slug – name är en fallback-matchare och ska
    // inte skapa en ny cachepost (det skulle bryta SSR-prefetchen).
    queryKey: ["company-offers", slug],
    queryFn: () => fetchCompanyOffers(slug, name),
    staleTime: STALE_TIME,
  });

/**
 * Hämtar en annonsör (elbolag) via slug samt bolagets aktiva, godkända
 * kampanjer. Används av elbolags- och elhandelssidorna så att båda mallarna
 * visar samma priser, jämförpriser och affiliatelänkar. `initialData` kan
 * matas från en route loader (SSR) för att undvika layout-hopp.
 */
export const useCompanyOffers = (
  slug: string | null | undefined,
  name?: string | null,
  initialData?: CompanyOffersResult,
) => {
  const { data, isLoading } = useQuery({
    ...companyOffersQueryOptions(slug ?? "", name),
    enabled: !!slug,
    ...(initialData !== undefined ? { initialData } : {}),
  });

  return {
    advertiser: data?.advertiser ?? null,
    plans: data?.plans ?? [],
    loading: !!slug && isLoading,
  };
};
